import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Room, RoomEvent, Track } from 'livekit-client'
import { Loader2, VideoOff } from 'lucide-react'
import { startAvatarSession, stopAvatarSession, fetchAvatarSpeech, type AvatarSessionInfo } from '@/api/avatar'

export type AvatarStatus = 'idle' | 'connecting' | 'live' | 'error'

export type TalkingAvatarHandle = {
  connect: () => Promise<void>
  disconnect: () => Promise<void>
  /** Resolves once the avatar has actually finished speaking (not just once audio was sent) —
   * lets a caller safely resume listening without picking up the avatar's own voice. */
  speak: (text: string) => Promise<void>
}

// Chunk raw bytes so each agent.speak message's base64 payload stays comfortably under the
// documented 1MB-per-packet ceiling (750KB raw → ~1MB base64).
const MAX_CHUNK_BYTES = 750_000

function chunkBase64(b64: string): string[] {
  const raw = atob(b64)
  if (raw.length <= MAX_CHUNK_BYTES) return [b64]
  const chunks: string[] = []
  for (let i = 0; i < raw.length; i += MAX_CHUNK_BYTES) {
    chunks.push(btoa(raw.slice(i, i + MAX_CHUNK_BYTES)))
  }
  return chunks
}

export const TalkingAvatar = forwardRef<
  TalkingAvatarHandle,
  { coach: string; className?: string; onStatusChange?: (status: AvatarStatus) => void }
>(function TalkingAvatar({ coach, className, onStatusChange }, ref) {
  const [status, setStatusState] = useState<AvatarStatus>('idle')
  const setStatus = (s: AvatarStatus) => { setStatusState(s); onStatusChange?.(s) }
  const videoRef = useRef<HTMLVideoElement>(null)
  const roomRef = useRef<Room | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const sessionRef = useRef<AvatarSessionInfo | null>(null)
  const wsConnectedRef = useRef(false)
  const audioElRef = useRef<HTMLAudioElement | null>(null)
  const speakResolveRef = useRef<(() => void) | null>(null)

  const disconnect = async () => {
    wsConnectedRef.current = false
    speakResolveRef.current?.()
    speakResolveRef.current = null
    try { wsRef.current?.close() } catch { /* */ }
    wsRef.current = null
    audioElRef.current?.remove()
    audioElRef.current = null
    try { await roomRef.current?.disconnect() } catch { /* */ }
    roomRef.current = null
    if (sessionRef.current) {
      stopAvatarSession(sessionRef.current.session_id)
      sessionRef.current = null
    }
    setStatus('idle')
  }

  const connect = async () => {
    setStatus('connecting')
    const session = await startAvatarSession(coach)
    if (!session) { setStatus('error'); return }
    sessionRef.current = session

    try {
      const room = new Room()
      room.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind === Track.Kind.Video && videoRef.current) {
          track.attach(videoRef.current)
        } else if (track.kind === Track.Kind.Audio) {
          // attach() returns a new <audio> element but does NOT insert it into the document —
          // without appending it, nothing plays.
          const el = track.attach()
          el.style.display = 'none'
          document.body.appendChild(el)
          audioElRef.current = el
        }
      })
      await room.connect(session.livekit_url, session.livekit_client_token)
      roomRef.current = room

      const ws = new WebSocket(session.ws_url)
      ws.addEventListener('message', (ev) => {
        try {
          const msg = JSON.parse(ev.data)
          if (msg.type === 'session.state_updated' && msg.state === 'connected') {
            wsConnectedRef.current = true
          }
          if (msg.type === 'agent.speak_ended' || msg.type === 'agent.idle_started') {
            speakResolveRef.current?.()
            speakResolveRef.current = null
          }
        } catch { /* non-JSON frame */ }
      })
      ws.addEventListener('close', () => { wsConnectedRef.current = false })
      wsRef.current = ws
      await new Promise<void>((resolve) => { ws.addEventListener('open', () => resolve()) })

      setStatus('live')
    } catch {
      setStatus('error')
      await disconnect()
    }
  }

  const speak = async (text: string) => {
    if (status !== 'live' || !wsConnectedRef.current || !wsRef.current) return
    const b64 = await fetchAvatarSpeech(text, coach)
    if (!b64 || !wsRef.current) return
    const ws = wsRef.current
    await new Promise<void>((resolve) => {
      speakResolveRef.current = resolve
      for (const chunk of chunkBase64(b64)) {
        ws.send(JSON.stringify({ type: 'agent.speak', audio: chunk }))
      }
      // Safety timeout in case the expected completion event never arrives.
      setTimeout(() => {
        if (speakResolveRef.current === resolve) { speakResolveRef.current = null; resolve() }
      }, 20000)
    })
  }

  useImperativeHandle(ref, () => ({ connect, disconnect, speak }), [status, coach])

  useEffect(() => () => { disconnect() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={className}>
      {/* Cropped like a real video call — the stock avatars are framed as a medium shot
          (head-to-waist, room visible); anchoring toward the top keeps the face large and
          centered the way a Zoom/FaceTime tile looks. Kept mild (1.2x, not the old 1.6x) — the
          source is a fixed 1280x720 stream, so scaling it up is pure pixel stretching with no new
          detail; the previous crop was magnifying HeyGen's compression softness noticeably. */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        className="h-full w-full rounded-2xl bg-black object-cover"
        style={{ transform: 'scale(1.2)', transformOrigin: '50% 22%' }}
      />
      {status !== 'live' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-2xl bg-black/80 text-white">
          {status === 'connecting' && <Loader2 className="size-6 animate-spin" />}
          {status === 'error' && <VideoOff className="size-6" />}
          <span className="text-xs">
            {status === 'connecting' ? 'Connecting…' : status === 'error' ? "Couldn't connect" : ''}
          </span>
        </div>
      )}
    </div>
  )
})
